import lodash from 'lodash';

import {
    LOAD_FULL_STUDY_OBJECT
} from '../actions/editStudy';

import { isDraftStudy } from '../../../common/util/studyHelpers';


/**
 * @typedef StudyTeamMember
 * @property {String} id
 * @property {String} userId
 * @property {Boolean} isClinicalStaff 
 * @property {String} nameOfResearcher
 */


export function loadStudy(state, action) {
    if (!action || action.type !== LOAD_FULL_STUDY_OBJECT) {
        return state;
    }

    const existingStudy = action.payload;
    const newState = lodash.cloneDeep(state);
    const existingStudyTeam = formatStudyTeam(existingStudy.studyTeam);
    const existingStudyMetadataGroupedByType = groupStudyMetadataValues(existingStudy.metadata);
    const existingStudyEssentials = combineMetadataValues(existingStudyMetadataGroupedByType.essentials);
    const existingStudyDetails = combineMetadataValues(existingStudyMetadataGroupedByType.binder);

    // Load existing study data for later change comparison
    lodash.set(newState, 'study.originalFields.studyId', existingStudy.id);
    lodash.set(newState, 'study.originalFields.binderId', existingStudy.binderId);
    lodash.set(newState, 'study.originalFields.studyName', existingStudy.name);
    lodash.set(newState, 'study.originalFields.studyType', existingStudy.studyType);
    lodash.set(newState, 'study.originalFields.studyTeam', existingStudy.studyTeam);
    lodash.set(newState, 'study.originalFields.studyEssentials', existingStudyMetadataGroupedByType.essentials);
    lodash.set(newState, 'study.originalFields.studyDetails', existingStudyMetadataGroupedByType.binder);
    lodash.set(newState, 'study.originalFields.studyStage', existingStudy.currentStage);

    // Load up workflow fields with existing workflow 
    lodash.set(newState, 'study.fields.studyName', existingStudy.name);
    lodash.set(newState, 'study.fields.studyType', existingStudy.studyType);
    lodash.set(newState, 'study.fields.studyTeam', existingStudyTeam);
    lodash.set(newState, 'study.fields.studyEssentials', existingStudyEssentials);
    lodash.set(newState, 'study.fields.studyDetails', existingStudyDetails);
    lodash.set(newState, 'study.fields.studyStage', existingStudy.currentStage);

    lodash.set(newState, 'study.isDraft', isDraftStudy(existingStudy));

    return newState;
}

/**
 * 
 * @param {Array<StudyTeamMember>} studyTeam 
 */
const formatStudyTeam = studyTeam => {
    return studyTeam.map((studyTeamMember) => {
        const teamMember = {
            id: studyTeamMember.id,
            userId: studyTeamMember.userId,
            sub: studyTeamMember.user.sub,
            displayName: studyTeamMember.user.displayName,
            studyRoleId: studyTeamMember.studyRoleId,
            nameOfResearcher: studyTeamMember.nameOfResearcher,
            value: {
                value: studyTeamMember.nameOfResearcher,
                label: studyTeamMember.nameOfResearcher
            },
        };

        // Add studyRole if it exists
        if(studyTeamMember.studyRole) {
            teamMember['studyRole'] = {
                id: studyTeamMember.studyRole.id,
                name: studyTeamMember.studyRole.name,
            };
        }

        return teamMember;
    }
    );
};


/**
 * Groups studyMetadata by type, then by descriptor.
 * @param {Array<StudyMetadata>} studyMetadata 
 * @returns {{[StudyMetaType]: {[descriptorId]: Array<StudyMetadata>}}}
 */
const groupStudyMetadataValues = (studyMetadata = []) => {
    return studyMetadata.reduce((acc, studyMetadata) => {
        const valuePath = (studyMetadata.descriptorId) ?
            `${studyMetadata.type}.${studyMetadata.descriptorId}`:
            `${studyMetadata.type}.${studyMetadata.name}`;
        const existingStudyMetadataByTypeByDescriptor = lodash.get(acc, valuePath, []);
        existingStudyMetadataByTypeByDescriptor.push(studyMetadata);
        return lodash.set(acc, valuePath, existingStudyMetadataByTypeByDescriptor);
    }, {});
};

/**
 * Combines individual study metadata values related by
 * descriptor. Also transforming the format of the value field.
 * Note: The data transformation step should be moved to the component
 * @param {{[descriptorId]: Array<StudyMetadata>}} existingStudyMetadataGroupedByType
 * @returns {Array<StudyMetadata>}
 */
export const combineMetadataValues = existingStudyMetadataGroupedByType => {
    return lodash.map(existingStudyMetadataGroupedByType, studyMetadataValuesByDescriptor => {
        const mainStudyMetadata = lodash.clone(studyMetadataValuesByDescriptor[0]);
        const SELECT_INPUT_TYPES = ['single-select', 'multi-select'];
        const values = studyMetadataValuesByDescriptor.map(studyMetadataValue =>
            SELECT_INPUT_TYPES.includes(studyMetadataValue.inputFieldType) ?
                ({ label: studyMetadataValue.value, value: studyMetadataValue.value }) :
                studyMetadataValue.value);

        lodash.unset(mainStudyMetadata, 'id');
        lodash.unset(mainStudyMetadata, 'studyId');
        mainStudyMetadata.value = values.length === 1 ? values[0] : values;

        return mainStudyMetadata;
    });
};
